/* eslint-disable react-hooks/exhaustive-deps */
'use client'

import { useAxios } from "@/hooks";
import { setCartData } from "@/redux/actions/user.action";
import { AppDispatch } from "@/redux/store";
import { endPoint, instance } from "@/utils";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

interface TProps {
    eventId: string;
    eventCode: string;
    cartData: any;
    onNext: () => void;
}

const useDateTime = ({ eventId, eventCode, cartData, onNext }: TProps) => {
    const [dates, setDates] = useState<any[]>([]);
    const [times, setTimes] = useState<any[]>([]);
    const [datesError, setDatesError] = useState(false);
    const [timesLoading, setTimesLoading] = useState(false);

    // Start in the loading state - otherwise the very first render (before the
    // fetch kicks off) flashes "No dates available" even when dates exist.
    const { fetchData, loading } = useAxios(true);

    // Selecting a new date cancels the previous slots request, so a slow
    // response for an earlier date can't overwrite the current date's slots.
    const timesRequest = useRef<AbortController | null>(null);

    const dispatch = useDispatch<AppDispatch>();

    const handleSelectDate = async (date: any) => {
        if (!date?.date) return;

        // Only one day is ever selected at a time - picking a day always resets
        // the cart (date, time and any tickets) so a leftover selection from a
        // previous day never carries over.
        dispatch(setCartData(date, {}, {}, eventCode));

        if (!date?.hasTime) {
            onNext();
            return;
        }

        await fetchEventTimes(date?.date);
    }

    const handleSelectTime = (time: any) => {
        if (!time) return;

        dispatch(setCartData(cartData?.selectedDate, time, {}, eventCode));

        onNext();
    }

    const fetchEventDates = async () => {
        if (!eventId) return;

        setDatesError(false);

        // The cart isn't reset just because this step remounted (e.g.
        // navigating back to date selection) - that used to wipe out
        // tickets the user had already picked. But if the cart still
        // belongs to a DIFFERENT event (left over from browsing/buying
        // elsewhere), it must not leak into this event's selection.
        if (cartData?.eventId && cartData.eventId !== eventCode) {
            dispatch(setCartData({}, {}, {}, eventCode));
        }

        // fetchData resolves (instead of throwing) with the error payload on
        // failure, so a failed/timed-out request has no `data.dates` array.
        const res: any = await fetchData({
            url: endPoint.FETCH_ACTIVE_DATES,
            method: 'GET',
            params: { eventId },
        });

        if (!Array.isArray(res?.data?.dates)) {
            setDates([]);
            setDatesError(true);
            return;
        }

        // Normalise both sides to local midnight - the API may send the date
        // as an ISO string or a timestamp, and comparing a string against a
        // number is always false, which filtered out every date.
        const today = new Date().setHours(0, 0, 0, 0);
        setDates(res.data.dates.filter((date: any) => {
            const day = new Date(date?.date).setHours(0, 0, 0, 0);
            return !Number.isNaN(day) && day >= today;
        }));
    }

    const fetchEventTimes = async (date: number) => {
        if (!eventId) return;

        timesRequest.current?.abort();
        const controller = new AbortController();
        timesRequest.current = controller;

        setTimes([]);
        setTimesLoading(true);

        // Slots have their own loading state (timesLoading), so call the axios
        // instance directly - going through useAxios here would toggle the
        // shared `loading` flag and swap the whole date list for a skeleton.
        try {
            const res: any = await instance.get(endPoint.FETCH_ACTIVE_SLOTS, {
                params: { eventId, date },
                signal: controller.signal,
            });

            setTimes(Array.isArray(res?.data?.slots) ? res.data.slots : []);
        } catch (error) {
            if (!controller.signal.aborted) console.error(error);
        } finally {
            if (!controller.signal.aborted) setTimesLoading(false);
        }
    }

    useEffect(() => {
        fetchEventDates();
        return () => timesRequest.current?.abort();
    }, [eventId]);

    return {
        loading,
        dates,
        datesError,
        times,
        timesLoading,
        handleSelectDate,
        handleSelectTime,
        retryFetchDates: fetchEventDates,
    }
}

export default useDateTime;
